/**
 * ApprovalFlowSection Component
 *
 * Approval settings of one document kind drawn as the flow a submitted document takes: the AI
 * policy, the chain, the fallback recipient and the end, under a switch that skips it all.
 */
import { useId, useState, type ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { AlertBanner, Button, ExpandableTextarea, Toggle } from '@insio/ui-toolkit';
import {
  ApprovalMode,
  type ApproverFallbackStrategy,
  type Permission,
  type UserRole
} from '@insio/shared-types';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { LifecycleDocument, LifecycleStepRef } from '../types';
import {
  chainChipOf,
  hasRecipientStep,
  nextStepOf,
  recipientOf,
  type FallbackDraft,
  type FallbackUser
} from './approval-flow';
import { ApprovalFlowChip, ApprovalFlowStep } from './ApprovalFlowStep';
import { ChainStepFields } from './ChainStepFields';
import { FallbackRecipientSelect } from './FallbackRecipientSelect';

const FLOW_KEY_PREFIX = 'features.systemSettings.automation.approvalFlow';

type ChainStep = 'chain' | 'recipient';

export interface ApprovalFlowSectionProps {
  document: LifecycleDocument;
  /** Feature translation prefix of the kind's approval settings (`flow`, `fallback`, …). */
  translationPrefix: string;
  testIdPrefix: string;
  mode: ApprovalMode;
  onModeChange: (mode: ApprovalMode) => void;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  isPromptDirty: boolean;
  isSavingPrompt: boolean;
  isResetting: boolean;
  isPreviewing: boolean;
  onSavePrompt: () => void;
  onOpenPreview: () => void;
  onOpenReset: () => void;
  onOpenHelp: () => void;
  /** The dry-run result under the policy buttons. */
  previewResult?: ReactNode;
  /** A notice under the header, e.g. the org-structure warning. */
  notice?: ReactNode;
  fallback: FallbackDraft;
  onFallbackChange: (draft: FallbackDraft) => void;
  /** The stored fallback user, named even when no longer among `users` (e.g. a removed member). */
  storedFallbackUser?: { readonly id: string; readonly displayName: string } | null;
  /** Everyone who may approve this kind; undefined until known, which locks the chain. */
  users: readonly FallbackUser[] | undefined;
  /** Who may approve could not be loaded, so the chain stays locked. */
  hasCandidatesError?: boolean;
  orgUnits: readonly { id: string; name: string }[];
  roles: readonly UserRole[];
  approvePermission: Permission;
  /** Chains this document kind can follow; all of them when undefined. */
  strategies?: readonly ApproverFallbackStrategy[];
  /** Opens another lifecycle step; the skipped notice links the next one. */
  onOpenStep?: (step: LifecycleStepRef) => void;
  /** Shows the settings with every switch and pick locked, and no save, dry-run or restore. */
  isReadOnly?: boolean;
}

export function ApprovalFlowSection({
  document,
  translationPrefix,
  testIdPrefix,
  mode,
  onModeChange,
  prompt,
  onPromptChange,
  isPromptDirty,
  isSavingPrompt,
  isResetting,
  isPreviewing,
  onSavePrompt,
  onOpenPreview,
  onOpenReset,
  onOpenHelp,
  previewResult,
  notice,
  fallback,
  onFallbackChange,
  storedFallbackUser,
  users,
  hasCandidatesError = false,
  orgUnits,
  roles,
  approvePermission,
  strategies,
  onOpenStep,
  isReadOnly = false
}: ApprovalFlowSectionProps) {
  const t = useFeatureTranslation(translationPrefix);
  const tFlow = useFeatureTranslation('systemSettings.automation.approvalFlow');
  const tHelp = useFeatureTranslation('systemSettings.automation.help');
  const tRole = useEnumTranslation('userRole');
  const id = useId();
  const policyBodyId = `${id}-policy`;
  const chainBodyId = `${id}-chain`;
  const recipientBodyId = `${id}-recipient`;

  const isSkipped = mode === ApprovalMode.SKIPPED;
  const isAiEnabled = mode === ApprovalMode.AI_AND_USER;
  // Null follows the AI switch: open while the AI runs, closed while it is off.
  const [isPolicyOpenPicked, setIsPolicyOpenPicked] = useState<boolean | null>(null);
  const isPolicyOpen = isPolicyOpenPicked ?? isAiEnabled;
  // Steps 2 and 3 are an accordion: opening one closes the other.
  const [openChainStep, setOpenChainStep] = useState<ChainStep | null>(null);
  const toggleChainStep = (step: ChainStep) =>
    setOpenChainStep(current => (current === step ? null : step));

  const isBusy = isSavingPrompt || isResetting;
  // A chain cannot be checked before those who may approve are known, so it is not edited;
  // nor is it by whoever may only read it.
  const isChainLocked = users === undefined || isReadOnly;
  // Whoever may not change the settings opens a step only to see it.
  const stepToggleLabel = isReadOnly ? tFlow('show') : tFlow('edit');
  const fallbackPrefix = `${translationPrefix}.fallback`;
  const fallbackTestIdPrefix = `${testIdPrefix}-fallback`;

  const handleAiChange = (next: boolean) => {
    setIsPolicyOpenPicked(next);
    onModeChange(next ? ApprovalMode.AI_AND_USER : ApprovalMode.USER_ONLY);
  };

  // The picked user by name, even one who is no longer among those who may approve.
  const pickedUserName =
    users?.find(user => user.id === fallback.userId)?.displayName ??
    (storedFallbackUser?.id === fallback.userId ? storedFallbackUser.displayName : undefined);
  const chainChip = chainChipOf(fallback, {
    userName: pickedUserName,
    orgUnitName: orgUnits.find(unit => unit.id === fallback.orgUnitId)?.name,
    roleLabel: fallback.role !== '' ? tRole(fallback.role) : undefined
  });
  const recipient = recipientOf(fallback);
  const recipientChip =
    recipient === 'USER'
      ? tFlow('recipientChip.USER', { name: pickedUserName ?? '' })
      : recipient === 'OWNER_PICKS'
        ? tFlow('recipientChip.OWNER_PICKS')
        : t('flow.recipientNone');

  const nextStep = nextStepOf(document);

  return (
    <div className='space-y-lg'>
      <div className='flex flex-wrap items-center justify-between gap-md'>
        <h2 className='text-xl font-bold text-foreground-base'>{t('title')}</h2>
        <Toggle
          checked={!isSkipped}
          onChange={next => onModeChange(next ? ApprovalMode.AI_AND_USER : ApprovalMode.SKIPPED)}
          disabled={isReadOnly}
          labelText={t('flow.master')}
          testId={`${testIdPrefix}-master-toggle`}
        />
      </div>

      {notice}

      {isSkipped ? (
        <AlertBanner
          variant='info'
          title={
            <Trans
              i18nKey={`${FLOW_KEY_PREFIX}.skipped`}
              components={{
                link: onOpenStep ? (
                  <button
                    type='button'
                    className='underline hover:opacity-70'
                    onClick={() => onOpenStep(nextStep)}
                    data-testid={`${testIdPrefix}-skipped-next-step`}
                  />
                ) : (
                  <span />
                )
              }}
            />
          }
          testId={`${testIdPrefix}-skipped-notice`}
        />
      ) : (
        <>
          {hasCandidatesError && (
            <AlertBanner
              variant='error'
              title={tFlow('candidatesLoadError')}
              testId={`${testIdPrefix}-candidates-error`}
            />
          )}
          <ol className='relative flex flex-col gap-sm pl-xl before:absolute before:bottom-sm before:left-[15px] before:top-sm before:w-0.5 before:bg-border-base'>
            <ApprovalFlowStep
              marker={1}
              isMuted={!isAiEnabled}
              header={t('flow.aiStep')}
              actions={
                <Toggle
                  checked={isAiEnabled}
                  onChange={handleAiChange}
                  disabled={isReadOnly}
                  labelText={isAiEnabled ? t('enabled.on') : t('enabled.off')}
                  size='sm'
                  testId={`${testIdPrefix}-enabled-toggle`}
                />
              }
              isOpen={isPolicyOpen}
              onToggle={() => setIsPolicyOpenPicked(!isPolicyOpen)}
              editLabel={stepToggleLabel}
              bodyId={policyBodyId}
              testId={`${testIdPrefix}-ai-section`}
            >
              <div className='flex flex-col gap-sm'>
                <ExpandableTextarea
                  label={t('prompt.label')}
                  aria-label={t('prompt.label')}
                  rows={8}
                  value={prompt}
                  onChange={onPromptChange}
                  disabled={isReadOnly}
                />
                <div className='flex flex-wrap items-center gap-sm'>
                  {!isReadOnly && (
                    <>
                      <Button
                        variant='primary'
                        onClick={onSavePrompt}
                        disabled={!isPromptDirty || isBusy}
                        testId={`${testIdPrefix}-save`}
                      >
                        {isSavingPrompt ? t('saving') : t('save')}
                      </Button>
                      <Button
                        variant='secondary'
                        onClick={onOpenPreview}
                        disabled={isPromptDirty || isBusy || isPreviewing}
                        title={isPromptDirty ? t('preview.unsavedChanges') : undefined}
                        testId={`${testIdPrefix}-preview-open`}
                      >
                        {isPreviewing ? t('preview.running') : t('preview.title')}
                      </Button>
                      <Button
                        variant='ghost'
                        onClick={onOpenReset}
                        disabled={isBusy}
                        testId={`${testIdPrefix}-reset`}
                      >
                        {isResetting ? t('resetting') : t('reset')}
                      </Button>
                    </>
                  )}
                  <Button
                    variant='ghost'
                    icon='circle-info'
                    onClick={onOpenHelp}
                    testId={`${testIdPrefix}-help-open`}
                  >
                    {tHelp('open')}
                  </Button>
                </div>
                {previewResult}
              </div>
            </ApprovalFlowStep>

            {isAiEnabled && (
              <li
                className='flex flex-wrap gap-sm text-xs text-foreground-muted'
                data-testid={`${testIdPrefix}-branch`}
              >
                <span className='rounded-full bg-background-base px-sm py-2xs'>
                  <Trans
                    i18nKey={`${FLOW_KEY_PREFIX}.branchMet`}
                    components={{ b: <strong className='font-semibold text-foreground-base' /> }}
                  />
                </span>
                <span className='rounded-full bg-background-base px-sm py-2xs'>
                  {t('flow.branchNotMet')}
                </span>
              </li>
            )}

            <ApprovalFlowStep
              marker={2}
              header={
                <>
                  {isAiEnabled ? t('flow.chainLeadAi') : t('flow.chainLeadUser')}{' '}
                  <ApprovalFlowChip
                    // The kind is the i18next context: a `_<kind>` key words a chip per kind.
                    label={tFlow(chainChip.key, { ...chainChip.values, context: document })}
                    isActive={openChainStep === 'chain'}
                    onClick={() => toggleChainStep('chain')}
                    controlsId={chainBodyId}
                    testId={`${testIdPrefix}-chain-chip`}
                  />
                </>
              }
              isOpen={openChainStep === 'chain'}
              onToggle={() => toggleChainStep('chain')}
              editLabel={stepToggleLabel}
              bodyId={chainBodyId}
              testId={`${testIdPrefix}-fallback-section`}
            >
              <ChainStepFields
                translationPrefix={fallbackPrefix}
                draft={fallback}
                onChange={onFallbackChange}
                users={users}
                orgUnits={orgUnits}
                roles={roles}
                approvePermission={approvePermission}
                strategies={strategies}
                disabled={isChainLocked}
                testIdPrefix={fallbackTestIdPrefix}
              />
            </ApprovalFlowStep>

            {hasRecipientStep(fallback.strategy) && (
              <ApprovalFlowStep
                marker={3}
                header={
                  <>
                    {tFlow(`fallbackLead.${recipient}`)}{' '}
                    <ApprovalFlowChip
                      label={recipientChip}
                      isActive={openChainStep === 'recipient'}
                      onClick={() => toggleChainStep('recipient')}
                      controlsId={recipientBodyId}
                      testId={`${testIdPrefix}-recipient-chip`}
                    />
                  </>
                }
                isOpen={openChainStep === 'recipient'}
                onToggle={() => toggleChainStep('recipient')}
                editLabel={stepToggleLabel}
                bodyId={recipientBodyId}
                testId={`${testIdPrefix}-recipient-section`}
              >
                <FallbackRecipientSelect
                  translationPrefix={fallbackPrefix}
                  draft={fallback}
                  onChange={onFallbackChange}
                  users={users}
                  roles={roles}
                  approvePermission={approvePermission}
                  disabled={isChainLocked}
                  testIdPrefix={fallbackTestIdPrefix}
                />
              </ApprovalFlowStep>
            )}

            <ApprovalFlowStep marker='end' header={t('flow.end')} testId={`${testIdPrefix}-end`} />
          </ol>

          <div className='flex flex-col gap-xs border-t border-border-base pt-md'>
            <Toggle
              checked={fallback.isSelfApprovalForbidden}
              onChange={checked =>
                onFallbackChange({ ...fallback, isSelfApprovalForbidden: checked })
              }
              labelText={t('flow.selfApproval.label')}
              disabled={isChainLocked}
              testId={`${fallbackTestIdPrefix}-self-approval-forbidden`}
            />
            <p className='text-sm text-foreground-muted'>{t('flow.selfApproval.hint')}</p>
          </div>
        </>
      )}
    </div>
  );
}
